import React, { type ReactNode, Suspense, useEffect } from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { AgentSessionsContext } from './AgentSessionsContext.ts';
import {
	SESSION_REFETCH_DELAYS_MS,
	usePageCommentAgentSessionsInner,
} from './usePageCommentAgentSessions.ts';

type AgentSessionsFetcherProps = {
	pageId: string;
	children: ReactNode;
	refetchOnMount?: boolean;
	preserveEmptyMarker?: boolean;
};

type AgentSessionsFetcherInnerProps = {
	pageId: string;
	children: ReactNode;
	refetchOnMount: boolean;
	preserveEmptyMarker: boolean;
};

// Delay before deleting the per-user `cc-agent-mention-sessions` marker when the sessions query
// is empty. Derived from the sessions-poll schedule so it always outlasts the final poll (+2s
// margin): a freshly-mentioned comment's session is created asynchronously by the backend, so the
// query is briefly empty while usePageCommentAgentSessionsInner polls. Deriving it from
// SESSION_REFETCH_DELAYS_MS (rather than hardcoding) keeps the two in lockstep if the poll
// schedule ever changes.
const MARKER_DELETE_DELAY_MS = Math.max(...SESSION_REFETCH_DELAYS_MS) + 2000;

/**
 * Inner component — calls useLazyLoadQuery (may suspend or throw on error).
 * Must not be rendered directly; always wrap with AgentSessionsFetcher.
 */
function AgentSessionsFetcherInner({
	pageId,
	children,
	refetchOnMount,
	preserveEmptyMarker,
}: AgentSessionsFetcherInnerProps) {
	const sessionMap = usePageCommentAgentSessionsInner(pageId, { refetchOnMount });
	const { userId } = useSessionData();
	const { updateContentProperty } = useUpdateContentProperty({
		contentId: pageId,
		contentType: 'page',
	});

	useEffect(() => {
		if (sessionMap.size > 0 || !userId || preserveEmptyMarker) {
			return;
		}
		// The sessions query came back empty. Don't delete the marker immediately: right after an
		// agent-mention comment is saved the backend creates the SessionAssociationPublic
		// asynchronously, so the query is briefly empty while usePageCommentAgentSessionsInner polls
		// for it. Defer the delete past that poll window — if a session shows up the map size
		// changes, this effect re-runs and the cleanup cancels the pending delete, so the
		// freshly-written marker is preserved (and the next page load still shows the session). Only
		// a marker that stays empty through the whole window is genuinely stale and gets cleaned up.
		const deleteMarkerTimer = setTimeout(() => {
			void updateContentProperty({
				propertyKey: `cc-agent-mention-sessions:${userId}`,
				isDelete: true,
			});
		}, MARKER_DELETE_DELAY_MS);
		return () => clearTimeout(deleteMarkerTimer);
		// Depend on sessionMap.size, not sessionMap: buildSessionMap returns a new Map
		// instance on every render even when data is unchanged, so using the Map reference
		// as a dep would fire this effect on every re-render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [preserveEmptyMarker, sessionMap.size, updateContentProperty, userId]);

	return (
		<AgentSessionsContext.Provider value={sessionMap}>{children}</AgentSessionsContext.Provider>
	);
}

/**
 * AgentSessionsFetcher — mounts only when hasCommentSessions is true (i.e. the
 * cc-agent-mention-sessions content property is set for this user on this page).
 * Calls usePageCommentAgentSessionsInner (which calls useLazyLoadQuery) and
 * provides the result via AgentSessionsContext.
 *
 * By conditionally mounting this component (not just conditionally calling the hook),
 * we guarantee zero Relay queries and zero Suspense when there are no agent sessions.
 *
 * If the query resolves with no sessions (allCommentAgentSessions is null or empty),
 * the per-user content property marker is deleted so subsequent page loads skip the fetch.
 *
 * Errors and suspensions from the inner Relay query are caught locally: the fallback
 * renders children without the AgentSessionsContext provider (empty Map default), so
 * comments remain fully functional even if the agent sessions fetch fails.
 *
 * Usage in ObjectCommentContainer:
 * ```tsx
 * if (hasCommentSessions) {
 *   return <AgentSessionsFetcher pageId={pageId}>{content}</AgentSessionsFetcher>;
 * }
 * return content;
 * ```
 */
export function AgentSessionsFetcher({
	pageId,
	children,
	refetchOnMount = false,
	preserveEmptyMarker = false,
}: AgentSessionsFetcherProps): React.JSX.Element {
	return (
		<GenericErrorBoundary attribution={Attribution.COMMENTS} renderOnError={() => <>{children}</>}>
			<Suspense fallback={null}>
				<AgentSessionsFetcherInner
					pageId={pageId}
					refetchOnMount={refetchOnMount}
					preserveEmptyMarker={preserveEmptyMarker}
				>
					{children}
				</AgentSessionsFetcherInner>
			</Suspense>
		</GenericErrorBoundary>
	);
}
